import React, { useState } from 'react';
import { updateOrganization, createOrganization } from '../api';
import PortalIcon from './PortalIcon';
import { Modal } from './PortalUI';

// Used both to create an organisation (`create`) and to change one: the same fields, the same pop-up.
// Logos are stored inline as a data: URI (no blob storage in this project) — kept well under the
// backend's 400KB cap so a phone photo doesn't silently fail; a real logo file is a few KB to begin
// with, so this only ever bites someone who picked the wrong file.
const MAX_LOGO_BYTES = 300 * 1024;

export default function EditOrgModal({ org, create = false, onClose, onSaved }) {
  const [name, setName] = useState(org?.name || '');
  const [cvr, setCvr] = useState(org?.cvr || '');
  const [contactName, setContactName] = useState(org?.contactName || '');
  const [contactEmail, setContactEmail] = useState(org?.contactEmail || '');
  const [contactPhone, setContactPhone] = useState(org?.contactPhone || '');
  const [brandingLogo, setBrandingLogo] = useState(org?.brandingLogo || '');
  const [logoError, setLogoError] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function handleLogoFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setLogoError('');
    if (!/^image\/(png|jpe?g|svg\+xml|webp)$/.test(file.type)) {
      setLogoError('Logoet skal være PNG, JPG, SVG eller WebP');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError('Filen er for stor — vælg et billede under 300 KB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setBrandingLogo(reader.result);
    reader.onerror = () => setLogoError('Kunne ikke læse filen — prøv igen');
    reader.readAsDataURL(file);
  }

  async function handleSave(e) {
    e.preventDefault();
    setError('');
    if (!name.trim()) {
      setError('Angiv et navn');
      return;
    }
    setSaving(true);
    try {
      const fields = { name: name.trim(), cvr: cvr.trim(), contactName: contactName.trim(), contactEmail: contactEmail.trim(), contactPhone: contactPhone.trim() };
      const result = create
        ? await createOrganization({ ...fields, cvr: fields.cvr || undefined, contactName: fields.contactName || undefined, contactEmail: fields.contactEmail || undefined, contactPhone: fields.contactPhone || undefined })
        : await updateOrganization(org.id, { ...fields, brandingLogo });
      if (!result.success) throw new Error(result.error || (create ? 'Kunne ikke oprette organisationen' : 'Kunne ikke gemme ændringerne'));
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      size="m"
      onClose={onClose}
      busy={saving}
      head={(
        <>
          <span className="pu-ico"><PortalIcon name="org" size={20} /></span>
          <div>
            <h2>{create ? 'Opret organisation' : name || 'Rediger organisation'}</h2>
            <small>{create ? 'En organisation samler en MSP\'s eller kundes tenants ét sted.' : 'Oplysninger om organisationen og dens kontaktperson.'}</small>
          </div>
        </>
      )}
    >
      <form onSubmit={handleSave}>
        <div className="pu-mb">
          <div className="pu-grid2">
            <div className="pu-col">
              <div className="pu-f"><label>Navn</label><input value={name} onChange={(e) => setName(e.target.value)} disabled={saving} autoFocus /></div>
              <div className="pu-f"><label>CVR-nummer <em>(valgfrit)</em></label><input value={cvr} onChange={(e) => setCvr(e.target.value)} disabled={saving} /></div>
              <div className="pu-f"><label>Kontaktperson</label><input value={contactName} onChange={(e) => setContactName(e.target.value)} disabled={saving} /></div>
            </div>
            <div className="pu-col">
              <div className="pu-f"><label>Email</label><input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} disabled={saving} /></div>
              <div className="pu-f"><label>Telefon</label><input type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} disabled={saving} /></div>
              {!create && (
                <div className="pu-f">
                  <span className="pu-lab">Rapport-logo</span>
                  <span className="pu-logo">
                    {brandingLogo ? <img src={brandingLogo} alt="Logo" /> : <span>Intet logo endnu</span>}
                    <label className="pu-link pu-logo__up">{brandingLogo ? 'Skift' : 'Upload'}<input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={handleLogoFile} disabled={saving} hidden /></label>
                    {brandingLogo && <button type="button" className="pu-link pu-link--bad" onClick={() => setBrandingLogo('')} disabled={saving}>Fjern</button>}
                  </span>
                  <em>Vises sammen med NordComplys logo på rapporterne for organisationens kunder.</em>
                  {logoError && <em className="pu-msg--bad">{logoError}</em>}
                </div>
              )}
            </div>
          </div>
          {error && <p className="pu-msg pu-msg--bad">{error}</p>}
        </div>
        <div className="pu-mf">
          <span className="pu-sp" />
          <button type="button" className="pu-btn" onClick={onClose} disabled={saving}>Annullér</button>
          <button type="submit" className="pu-btn pu-btn--p" disabled={saving}>{saving ? 'Gemmer…' : create ? 'Opret organisation' : 'Gem ændringer'}</button>
        </div>
      </form>
    </Modal>
  );
}
